import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Paperclip, X } from 'lucide-react';
import { SUPPORT_ATTACHMENT_ACCEPT } from '../../utils/documentFile';
import { MAX_SUPPORT_ATTACHMENTS, addAttachments, formatFileSize } from './supportTypes';

interface AttachmentPickerProps {
  files: File[];
  onChange: (files: File[]) => void;
  onError: (message: string) => void;
  disabled?: boolean;
  /** Kompakt fuer die Chat-Eingabe: nur der Knopf, ohne Ablagezone. */
  compact?: boolean;
}

/** Auswahl von bis zu fuenf Anhaengen per Knopf oder Drag & Drop, mit Liste der gewaehlten Dateien. */
export default function AttachmentPicker({ files, onChange, onError, disabled, compact }: AttachmentPickerProps) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const handleAdd = async (added: File[]) => {
    if (added.length === 0) return;
    const result = await addAttachments(files, added);
    if ('error' in result) onError(result.error);
    else onChange(result.files);
    if (inputRef.current) inputRef.current.value = '';
  };

  const input = (
    <input
      ref={inputRef}
      type="file"
      multiple
      accept={SUPPORT_ATTACHMENT_ACCEPT}
      className="hidden"
      onChange={(e) => handleAdd(Array.from(e.target.files ?? []))}
    />
  );

  const full = files.length >= MAX_SUPPORT_ATTACHMENTS;

  if (compact) {
    return (
      <>
        {input}
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={disabled || full}
          onClick={() => inputRef.current?.click()}
          title={t('support:form.addFiles')}
        >
          <Paperclip size={16} />
          <span className="hidden sm:inline">{t('support:form.addFiles')}</span>
        </button>
      </>
    );
  }

  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setDragging(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        if (!disabled) handleAdd(Array.from(e.dataTransfer.files));
      }}
      className={clsx('space-y-2 rounded-xl border border-dashed p-3 transition-colors', dragging ? 'border-primary-400 bg-primary-500/10' : 'border-dark-600')}
    >
      {input}
      <SelectedFiles files={files} onChange={onChange} />
      <button type="button" className="btn btn-ghost btn-sm" disabled={disabled || full} onClick={() => inputRef.current?.click()}>
        <Paperclip size={16} />
        {t('support:form.addFiles')}
      </button>
      <p className="text-xs text-dark-400">{t('support:form.attachmentHint', { max: MAX_SUPPORT_ATTACHMENTS })}</p>
    </div>
  );
}

/** Die gewaehlten Dateien als Chips - fuer die kompakte Variante, die die Liste ueber dem Eingabefeld zeigt. */
export function SelectedFiles({ files, onChange }: { files: File[]; onChange: (files: File[]) => void }) {
  const { t } = useTranslation();
  if (files.length === 0) return null;
  return (
    <ul className="flex flex-wrap gap-1.5">
      {files.map((file, index) => (
        <li key={`${file.name}-${index}`} className="inline-flex max-w-full items-center gap-1 rounded-full border border-dark-600 bg-dark-800/70 py-0.5 pl-2.5 pr-1 text-xs text-dark-200">
          <Paperclip size={12} className="shrink-0 text-dark-400" />
          <span className="truncate">{file.name}</span>
          <span className="shrink-0 text-dark-400">{formatFileSize(file.size)}</span>
          <button
            type="button"
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-dark-400 hover:bg-dark-600 hover:text-dark-100"
            onClick={() => onChange(files.filter((_, i) => i !== index))}
            aria-label={t('support:form.removeFile', { name: file.name })}
            title={t('support:form.removeFile', { name: file.name })}
          >
            <X size={12} />
          </button>
        </li>
      ))}
    </ul>
  );
}
